import { type FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { customPaymentMethods } from "@jti/fe-common";
import { deletePaymentTokenFromStorage } from "@jti/store/utils";

import { PolicyCheckFieldsWrapper } from "@checkout/components/PolicyCheckFieldsWrapper";
import usePgsClient from "@checkout/hooks/payment/usePgsClient";

import { PaymentPagePayButtons } from "../PaymentPagePayButtons";
import { PaymentPageSecureBlock } from "../PaymentPageSecureBlock";

import styles from "./PaymentPageFooter.module.scss";

type PaymentPageFooterProps = {
  paymentMethodCode: string;
} & React.HTMLAttributes<HTMLDivElement>;

export const PaymentPageFooter: FC<PaymentPageFooterProps> = ({ paymentMethodCode, ...restProps }) => {
  const { orderId } = useParams<{ orderId: string }>();
  const { consentsConfig } = useCheckoutConfiguration();
  const [isPolicyRead, setIsPolicyRead] = useState<boolean>(false);
  const payButtonContainerRef = useRef<HTMLDivElement>(null);

  usePgsClient({
    ref: payButtonContainerRef,
    paymentCode: paymentMethodCode,
    onUnmount: deletePaymentTokenFromStorage,
    buttonClassName: "payButton",
    orderId,
  });

  const jtiPolicyConsentCheckboxEnabled = !consentsConfig?.policyAndTermsConsentDisabled;
  const isPayOnDeliverySelected = paymentMethodCode === customPaymentMethods.payOnDelivery;
  const showPolicyCheck = isPayOnDeliverySelected || jtiPolicyConsentCheckboxEnabled;

  return (
    <div className={styles.container} {...restProps}>
      {showPolicyCheck && (
        <PolicyCheckFieldsWrapper
          setIsPolicyRead={setIsPolicyRead}
          className={styles.policyCheckField}
          data-testid="policy-fields-wrapper"
        />
      )}
      <PaymentPagePayButtons
        isPolicyRead={isPolicyRead}
        paymentMethodCode={paymentMethodCode}
        isPayOnDelivery={isPayOnDeliverySelected}
        showPolicyCheck={showPolicyCheck}
        ref={payButtonContainerRef}
      />

      <PaymentPageSecureBlock />
    </div>
  );
};
